import { useState } from "react";
import { Phone, Mail, User, UserPlus } from "lucide-react";
import api from "../../../../shared/services/api";

/**
 * Renderiza um ou mais contatos compartilhados dentro de MessageBubble.
 * Exibe avatar (inicial), nome, telefones clicáveis e botão "Salvar como cliente".
 *
 * @param {{ contacts: Array }} props
 *   contacts: array no formato Meta contacts[]
 */
export default function ContactCard({ contacts = [] }) {
  return (
    <div className="flex flex-col gap-2 w-60">
      {contacts.map((contato, idx) => (
        <ContatoItem key={idx} contato={contato} />
      ))}
    </div>
  );
}

function ContatoItem({ contato }) {
  const nome   = contato?.name?.formatted_name ?? contato?.name?.first_name ?? "Contato";
  const fones  = contato?.phones  ?? [];
  const emails = contato?.emails  ?? [];

  const [salvando, setSalvando] = useState(false);
  const [salvo,    setSalvo]    = useState(false);
  const [erro,     setErro]     = useState(null);

  async function handleSalvar() {
    if (salvando || salvo) return;
    setSalvando(true);
    setErro(null);
    try {
      const telefone = fones[0]?.phone  ?? null;
      const email    = emails[0]?.email ?? null;
      await api.post("/clientes", { nome, telefone, email, ativo: true });
      setSalvo(true);
    } catch (err) {
      setErro(err?.response?.data?.error ?? "Erro ao salvar contato.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="rounded-lg border border-white/[0.08] bg-surface-2 px-3 py-2.5">
      {/* Avatar + Nome */}
      <div className="flex items-center gap-2.5 mb-2">
        <div
          className="w-8 h-8 rounded-full bg-brand-800 text-brand-200 flex items-center justify-center text-xs font-bold flex-shrink-0 select-none"
          aria-hidden="true"
        >
          {nome[0]?.toUpperCase() ?? <User className="w-3.5 h-3.5" />}
        </div>
        <p className="text-xs font-semibold text-ink-1 truncate">{nome}</p>
      </div>

      {/* Telefones */}
      {fones.length > 0 && (
        <ul className="flex flex-col gap-1 mb-1.5" aria-label="Telefones">
          {fones.map((f, i) => (
            <li key={i} className="flex items-center gap-1.5">
              <Phone className="w-3 h-3 text-ink-4 flex-shrink-0" aria-hidden="true" />
              <a
                href={`tel:${f.phone}`}
                className="text-xs text-brand-400 hover:text-brand-300 transition-colors truncate focus:outline-none focus-visible:underline"
              >
                {f.phone}
              </a>
            </li>
          ))}
        </ul>
      )}

      {/* Emails */}
      {emails.length > 0 && (
        <ul className="flex flex-col gap-1 mb-2" aria-label="Emails">
          {emails.map((e, i) => (
            <li key={i} className="flex items-center gap-1.5">
              <Mail className="w-3 h-3 text-ink-4 flex-shrink-0" aria-hidden="true" />
              <a
                href={`mailto:${e.email}`}
                className="text-xs text-brand-400 hover:text-brand-300 transition-colors truncate focus:outline-none focus-visible:underline"
              >
                {e.email}
              </a>
            </li>
          ))}
        </ul>
      )}

      {/* Botão salvar como cliente */}
      {!salvo ? (
        <button
          type="button"
          onClick={handleSalvar}
          disabled={salvando}
          aria-label={`Salvar ${nome} como cliente`}
          className="mt-1 w-full flex items-center justify-center gap-1.5 text-xs text-ink-2 bg-surface-3 hover:bg-surface-4 border border-white/[0.12] rounded py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
        >
          <UserPlus className="w-3 h-3" aria-hidden="true" />
          {salvando ? "Salvando..." : "Salvar como cliente"}
        </button>
      ) : (
        <p className="mt-1 text-xs text-emerald-400 text-center">
          Cliente salvo com sucesso
        </p>
      )}

      {erro && (
        <p className="mt-1 text-xs text-red-400 text-center" role="alert">
          {erro}
        </p>
      )}
    </div>
  );
}
